/*
 * Event date labels on the EVENT's own calendar (CMT-2026-10-28 mytrumin-
 * multiday-range; governing DESIGN-HEAT-WINDOWS-BOX-OFFICE-V1, LOCKED).
 *
 * A multi-day event (Apex Games Abu Dhabi, Sat 14 – Sun 15 Nov 2026) used to
 * render only its START day everywhere, so a Doubles order (Doubles race on
 * the Sunday) read "Sat 14 Nov 2026". Rules here:
 *  - The DAY of an instant is taken in the event's IANA zone (settings V24
 *    cascade, carried by my-api as `eventTimeZone`); absent/invalid ⇒ the
 *    programme's fixed UTC+04:00 fallback (the same fallback my-api uses).
 *  - A multi-day event (start and end on different event-local days) renders
 *    its RANGE: "Sat 14 – Sun 15 Nov 2026", "Fri 30 Oct – Sun 1 Nov 2026",
 *    "Thu 31 Dec 2026 – Fri 1 Jan 2027". One day ⇒ "Sat 14 Nov 2026".
 *  - A TICKET shows its own day/window ONLY from data (my-api
 *    `ticketWindowStartsAt/EndsAt` = the settings heats its ticket type sits
 *    in). Never inferred from a ticket's name; absent ⇒ the event range.
 */

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The programme's event-local fallback offset (Gulf, no DST) — my-api's
 *  EVENT_LOCAL_OFFSET_MS / FALLBACK_EVENT_TIMEZONE. */
const FALLBACK_OFFSET_MS = 4 * 60 * 60 * 1000

interface LocalDay { y: number; m: number; d: number; wd: number }

function validZone(zone: string | null | undefined): zone is string {
  if (!zone || typeof zone !== 'string' || zone.trim() === '') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** The event-local calendar day of an instant (zone when valid, else UTC+4). */
function localDay(d: Date, zone: string | null | undefined): LocalDay {
  if (validZone(zone)) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric',
    }).formatToParts(d)
    const n = (t: string) => Number(parts.find((p) => p.type === t)?.value)
    const y = n('year'), m = n('month') - 1, day = n('day')
    // Weekday from the calendar date itself (UTC math on a date-only value).
    return { y, m, d: day, wd: new Date(Date.UTC(y, m, day)).getUTCDay() }
  }
  const s = new Date(d.getTime() + FALLBACK_OFFSET_MS)
  return { y: s.getUTCFullYear(), m: s.getUTCMonth(), d: s.getUTCDate(), wd: s.getUTCDay() }
}

const sameDay = (a: LocalDay, b: LocalDay) => a.y === b.y && a.m === b.m && a.d === b.d
const full = (x: LocalDay) => `${DAY[x.wd]} ${x.d} ${MON[x.m]} ${x.y}`

/** Canon single day "Sun 16 Aug 2026" on the event's calendar; '' when the
 *  instant is absent/unparseable (the caller then shows nothing). */
export function formatEventDate(iso: string | null | undefined, zone?: string | null): string {
  const d = parse(iso)
  return d ? full(localDay(d, zone)) : ''
}

/** Date label for [start, end] on the event's calendar. One local day (or no/
 *  unparseable/earlier end) ⇒ the single day; same month ⇒ "Sat 14 – Sun 15
 *  Nov 2026"; same year ⇒ "Fri 30 Oct – Sun 1 Nov 2026"; else both full. */
export function formatEventRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  zone?: string | null,
): string {
  const si = parse(startIso)
  if (!si) return ''
  const s = localDay(si, zone)
  const ei = parse(endIso)
  if (!ei || ei.getTime() < si.getTime()) return full(s)
  const e = localDay(ei, zone)
  if (sameDay(s, e)) return full(s)
  if (s.y === e.y && s.m === e.m) return `${DAY[s.wd]} ${s.d} – ${DAY[e.wd]} ${e.d} ${MON[s.m]} ${s.y}`
  if (s.y === e.y) return `${DAY[s.wd]} ${s.d} ${MON[s.m]} – ${DAY[e.wd]} ${e.d} ${MON[e.m]} ${e.y}`
  return `${full(s)} – ${full(e)}`
}

/** True only when BOTH instants parse and fall on different event-local days
 *  (end after start). Unknown end ⇒ false (single-day behaviour, unchanged). */
export function isMultiDay(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  zone?: string | null,
): boolean {
  const si = parse(startIso), ei = parse(endIso)
  if (!si || !ei || ei.getTime() < si.getTime()) return false
  return !sameDay(localDay(si, zone), localDay(ei, zone))
}

/** Everything a date label can draw on — every field optional/nullable so an
 *  older my-api (no zone / no window) degrades to today's behaviour. */
export interface EventDateFacts {
  eventStartsAt?: string | null
  eventEndsAt?: string | null
  eventTimeZone?: string | null
  /** The ticket's own window (settings heats for its ticket type, via my-api).
   *  Present ONLY when data carries it — never inferred. */
  ticketWindowStartsAt?: string | null
  ticketWindowEndsAt?: string | null
}

/** The ticket's own day/window label when data carries it, else ''. */
export function ticketWindowLabel(f: EventDateFacts): string {
  if (!parse(f.ticketWindowStartsAt)) return ''
  return formatEventRange(f.ticketWindowStartsAt, f.ticketWindowEndsAt ?? f.ticketWindowStartsAt, f.eventTimeZone)
}

/** Date label for ONE ticket of an event:
 *  - multi-day event ⇒ the ticket's own window when known, else the event RANGE;
 *  - single-day / unknown-end event ⇒ `fallback` (the caller's existing
 *    single-date text, e.g. the box-office `when`) else the start day —
 *    single-day rendering is unchanged. */
export function ticketDateLabel(f: EventDateFacts, fallback = ''): string {
  return multiDayTicketLabel(f) || fallback || formatEventDate(f.eventStartsAt, f.eventTimeZone)
}

/** The multi-day override ONLY: for a multi-day event the ticket's own window
 *  (when data carries it) else the event range; '' for a single-day/unknown
 *  event so callers keep their existing single-date text untouched. */
export function multiDayTicketLabel(f: EventDateFacts): string {
  if (!isMultiDay(f.eventStartsAt, f.eventEndsAt, f.eventTimeZone)) return ''
  return ticketWindowLabel(f) || formatEventRange(f.eventStartsAt, f.eventEndsAt, f.eventTimeZone)
}
